import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';

import _debounce from 'lodash/debounce';

import Button from 'components/Button';
import Message from 'components/Message';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import When from 'components/When';
import { PFE_ICONS } from 'constants/icons';
import { PERMISSION } from 'constants/logic';
import { schedulePropTypes } from 'constants/propTypes';
import { classNameWithModifiers } from 'helpers/class-name';
import { userHasPermission } from 'helpers/core';

import { messages } from './ScheduleListItem.messages';

const RESIZE_DEBOUNCE_LENGTH = 16 * 2; // 30 frames per second
const SELECTED_POPOUT_WIDTH = 16;

const RenderBadges = ({ isCancelled, reservationsStopped, withMods }) => {
  let badge;

  if (reservationsStopped) {
    badge = <Message message={messages.reservationsStopped} />;
  } else if (isCancelled) {
    badge = <Message message={messages.isCancelled} />;
  } else {
    return null;
  }

  return (
    <div className={withMods('schedule-list__item-column', { badges: true })}>
      <div className="schedule-list__badge">{badge}</div>
    </div>
  );
};

RenderBadges.propTypes = {
  isCancelled: PropTypes.bool,
  reservationsStopped: PropTypes.bool,
  withMods: PropTypes.func,
};

const RenderReservations = ({
  classpassBooked,
  classpassTotal,
  hasIntegrationData,
  maxCapacity,
  totalBooked,
  withMods,
}) => {
  return (
    <>
      {hasIntegrationData && (
        <div className={withMods('schedule-list__item-column', { all: true })}>
          <Message message={messages.allBookings} values={{ totalBooked, maxCapacity }} />
        </div>
      )}
      <div className={withMods('schedule-list__item-column', { cp: true, hasIntegrationData })}>
        <Message
          message={messages.cpAllocation}
          values={{
            classpassBooked,
            classpassTotal,
          }}
        />
      </div>
    </>
  );
};

RenderReservations.propTypes = {
  classpassBooked: PropTypes.number,
  classpassTotal: PropTypes.number,
  hasIntegrationData: PropTypes.bool,
  maxCapacity: PropTypes.number,
  totalBooked: PropTypes.number,
  withMods: PropTypes.func,
};

const ScheduleListItem = ({
  actOnOpportunity,
  handleAddSpot,
  hasMoreSchedules,
  isDesktop,
  isLastItem,
  isLoadingMoreSchedules,
  isSelected = false,
  loadMoreSchedules,
  permissions = [],
  registerNode,
  schedule,
  setSelectedSchedule = () => {},
  tab,
  venueId,
  visible,
}) => {
  // TODO(ralph): Refactor the way we store opportunities after we remove them from the reports endpoint
  // so we don't have to do hacky things like this. Many attempts were made to use the local storage cache
  // and they failed
  const [opportunityActedOn, setOpportunityActedOn] = useState(false);
  const [width, setWidth] = useState(0);
  const node = useRef(null);

  const {
    classpassAlmostSoldOut,
    classpassSoldOut,
    classpassSpotsUnfilled,
    duration,
    hasIntegrationData,
    isCancelled,
    isDisabled,
    isFull,
    isInThePast,
    isLocked,
    opportunity,
    reservationsStopped,
    resource,
    scheduleId,
    scheduleName,
    smartspotEnabled,
    spots,
    startDate,
    startDateTime = new Date(),
  } = schedule;
  const { classpassBooked, classpassDemand, classpassTotal, maxCapacity, totalBooked } = spots;
  const { resourceName: instructorName } = resource;

  const measureWidth = _debounce(
    () => {
      const { current } = node;
      if (current) {
        setWidth(current.offsetWidth);
      }
    },
    RESIZE_DEBOUNCE_LENGTH,
    {
      leading: false,
      trailing: true,
    }
  );

  useLayoutEffect(() => {
    const { current } = node;

    if (current) {
      if (scheduleId) {
        registerNode(scheduleId, isInThePast, current);
      }
      window.addEventListener('resize', measureWidth);
      measureWidth();
    }

    return () => {
      window.removeEventListener('resize', measureWidth);
    };
  }, [scheduleId, registerNode, isInThePast, measureWidth]);

  const isInViewport = element => {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  useEffect(() => {
    // This is a workaround for the InfiniteScroll in ScheduleList not triggering when the last item
    // is visible in the viewport due to filters. The InfiniteScroll component is only triggered when the user scrolls,
    // so we need to trigger it manually when the last item is visible.
    if (node.current && isLastItem && hasMoreSchedules && !isLoadingMoreSchedules) {
      if (isInViewport(node.current)) {
        loadMoreSchedules();
      }
    }
  }, [isLoadingMoreSchedules, isLastItem]);

  const handleSetAsSelected = e => {
    e.preventDefault();
    setSelectedSchedule({
      scheduleId,
      venueId,
      tab,
      startDate,
      focus: 'detail',
    });
  };

  const withMods = (base, obj) => {
    const mods = { isSelected, isLocked, ...obj };
    return classNameWithModifiers(base, mods);
  };

  const handleActOnOpportunity = event => {
    event.preventDefault();
    actOnOpportunity();
    setOpportunityActedOn(true);
  };

  const hasPermission = perm => {
    return userHasPermission({ permissions }, perm);
  };

  const style = {};
  if (width && isSelected && visible && isDesktop) {
    style.transform = `scale(${(width + SELECTED_POPOUT_WIDTH) / width}, 1)`;
  }

  const hasBadge = isCancelled || reservationsStopped;
  const hasSpotManagePermission = hasPermission(PERMISSION.DISPLAY_SPOT_MANAGEMENT);
  const recommendation = opportunity ? opportunity.recommendation : false;
  const showOpportunityButton =
    hasSpotManagePermission &&
    recommendation &&
    !isInThePast &&
    !isLocked &&
    !opportunityActedOn &&
    !reservationsStopped &&
    !smartspotEnabled;
  const showSSAddSpotButton =
    hasSpotManagePermission &&
    !isFull &&
    !isInThePast &&
    !isLocked &&
    (classpassSpotsUnfilled === 0 || classpassSpotsUnfilled === Infinity) &&
    smartspotEnabled;
  const showAddSpotButton =
    hasSpotManagePermission &&
    !isFull &&
    !isInThePast &&
    !isLocked &&
    (classpassSpotsUnfilled === 0 || classpassSpotsUnfilled === Infinity) &&
    !smartspotEnabled &&
    !recommendation &&
    classpassDemand > 0;

  const showDemandNotification =
    classpassDemand > 0 && (showOpportunityButton || showSSAddSpotButton || showAddSpotButton);

  return (
    // this needed to be a div instead of a because the tooltip is an anchor and we can't hav nested anchor tags
    <div
      ref={node}
      aria-label={`${startDateTime}: ${scheduleName}`}
      className={withMods('schedule-list__item')}
      onClick={handleSetAsSelected}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleSetAsSelected(e);
        }
      }}
      role="link"
      tabIndex={0}>
      <div className={withMods('schedule-list__item-viewport', { visible, isInThePast, isLastItem })} style={style} />
      <div className={withMods('schedule-list__item-container', { isInThePast, isDisabled })}>
        <div className="schedule-list__item-container-row">
          {showDemandNotification && (
            <div className={withMods('schedule-list__item-column', { notification: true })}>
              <TooltipToggle
                name={`tooltip-wait-notification-bell-${scheduleId}`}
                className="schedule-list__notification-bell"
                aria-label={`${classpassDemand} waiting for spot`}>
                <Isvg src={PFE_ICONS.notification} />
              </TooltipToggle>
              <TooltipContent name={`tooltip-wait-notification-bell-${scheduleId}`}>
                <Message
                  message={messages.hasDemandTooltip}
                  values={{ value: classpassDemand, b: chunk => <strong>{chunk}</strong> }}
                />
              </TooltipContent>
            </div>
          )}
          {!showDemandNotification && (
            <div className={withMods('schedule-list__item-column', { smartSpot: true })}>
              <Message message={messages.smartSpotOn}>
                {title => (
                  <div
                    title={title}
                    className={withMods('schedule-list__ss-indicator', {
                      smartspotEnabled,
                      isDisabled,
                      isCancelled,
                    })}
                  />
                )}
              </Message>
            </div>
          )}
          <div className={withMods('schedule-list__item-column', { info: true })}>
            <Message message={messages.durationMinutes} values={{ duration }}>
              {durationMinutes => {
                return (
                  <p className={withMods('schedule-list__start-time')} title={durationMinutes}>
                    <Message format="time" value={startDateTime} />
                  </p>
                );
              }}
            </Message>
          </div>
          {hasBadge ? (
            <RenderBadges isCancelled={isCancelled} reservationsStopped={reservationsStopped} withMods={withMods} />
          ) : (
            <RenderReservations
              classpassBooked={classpassBooked}
              classpassTotal={classpassTotal}
              hasIntegrationData={hasIntegrationData}
              maxCapacity={maxCapacity}
              totalBooked={totalBooked}
              withMods={withMods}
            />
          )}
        </div>
        <div className="schedule-list__item-container-row">
          <div className={withMods('schedule-list__item-column', { info: true })}>
            <p className="schedule-list__title" title={scheduleName}>
              {scheduleName}
            </p>
          </div>
          <div className={withMods('schedule-list__item-column', { biline: true })} />
        </div>
        <div className="schedule-list__item-container-row">
          <div className={withMods('schedule-list__item-column', { info: true })}>
            <p className={withMods('schedule-list__instructor', { isInThePast, isDisabled })}>
              {instructorName || <Message message={messages.noInstructor} />}
            </p>
          </div>
          <div className={withMods('schedule-list__item-column', { biline: true })}>
            {isDisabled && !isCancelled && !reservationsStopped && (
              <div className="schedule-list__badge">
                <Message message={messages.isDisabled} />
              </div>
            )}
            {!isInThePast && !isLocked && classpassAlmostSoldOut && (
              <div className={withMods('schedule-list__badge', { classpassAlmostSoldOut })}>
                <Message message={messages.almostFull}>
                  {text => (
                    <span
                      className={classNameWithModifiers('schedule-list__reservations-status-text', {
                        classpassAlmostSoldOut,
                      })}>
                      {text}
                    </span>
                  )}
                </Message>
              </div>
            )}
            {!isInThePast && !isLocked && classpassSoldOut && (
              <div className={withMods('schedule-list__badge', { classpassSoldOut })}>
                <Message message={messages.spotsFull}>
                  {text => (
                    <span
                      className={classNameWithModifiers('schedule-list__reservations-status-text', {
                        classpassSoldOut,
                      })}>
                      {text}
                    </span>
                  )}
                </Message>
              </div>
            )}
          </div>
        </div>
        <When predicate={showOpportunityButton}>
          <div className="schedule-list__item-container-row schedule-list__item-container-row--opportunity">
            <Button
              className="schedule-list__opportunity-button"
              styled="primary-outline"
              onClick={handleActOnOpportunity}
              data-cypress-recommendation={recommendation}>
              <Message message={messages.opportunityButton} values={{ recommendation }} />
            </Button>
          </div>
        </When>
        <When predicate={showAddSpotButton || showSSAddSpotButton}>
          <div className="schedule-list__item-container-row schedule-list__item-container-row--ss-add-spot">
            <Button className="schedule-list__ss-add-spot-button" styled="primary-outline" onClick={handleAddSpot}>
              <Message message={messages.addSpotButton} />
            </Button>
          </div>
        </When>
      </div>
    </div>
  );
};

ScheduleListItem.propTypes = {
  actOnOpportunity: PropTypes.func,
  classpassAlmostSoldOut: PropTypes.bool,
  classpassSoldOut: PropTypes.bool,
  handleAddSpot: PropTypes.func,
  hasMoreSchedules: PropTypes.bool,
  isDesktop: PropTypes.bool,
  isSelected: PropTypes.bool,
  isLastItem: PropTypes.bool,
  isLoadingMoreSchedules: PropTypes.bool,
  loadMoreSchedules: PropTypes.func,
  permissions: PropTypes.arrayOf(PropTypes.string),
  registerNode: PropTypes.func,
  schedule: PropTypes.shape(schedulePropTypes),
  setSelectedSchedule: PropTypes.func,
  startDateTime: PropTypes.instanceOf(Date),
  tab: PropTypes.string,
  venueId: PropTypes.number,
  visible: PropTypes.bool,
};

export default ScheduleListItem;
